<template>
    <ul class="events-rows container hd-container">
        <li v-for="event in items" :key="event.id" class="events-row">
            <!-- The row is a plain container, not one big <a>. Editors put links
                 inside Description, and an <a> nested in an <a> is invalid — the
                 browser splits them and the inner link stops working. Instead the
                 title's link stretches over the whole row (see .events-row-title-link
                 ::after), which keeps the large click target, keeps the markup
                 valid, and leaves description links clickable in their own right. -->
            <div class="events-row-link">
                <div class="events-row-when">
                    <span class="events-row-day">{{ dayLabel(event) }}</span>
                    <div class="events-row-when-inner">
                        <span class="events-row-time">{{ event.fields['Time'] }}</span>
                        <span v-if="event.fields['Event Type']" class="events-row-cat">
                            {{ event.fields['Event Type'] }}
                        </span>
                    </div>
                </div>

                <div class="events-row-body">
                    <h3 class="events-row-title">
                        <NuxtLink :to="`/events/${event.id}`" class="events-row-title-link">
                            {{ event.fields['Program Title'] || event.fields['Name'] }}
                        </NuxtLink>
                        <span v-if="status(event) === 'live'" class="events-row-status is-live">Happening now</span>
                        <span v-else-if="status(event) === 'ended'" class="events-row-status is-ended">Ended</span>
                    </h3>

                    <!-- Descriptions come from Airtable with light markup in them
                         (links and italics), so they render as HTML rather than
                         showing escaped tags. Same treatment the neighborhood and
                         site descriptions already get. -->
                    <p
                        v-if="event.fields['Description']"
                        class="events-row-desc"
                        v-html="event.fields['Description']"
                    ></p>

                    <div class="events-row-venue-row">
                        <span class="events-row-venue">
                            <SvgsThinMarker class="events-row-pin" />
                            {{ venue(event) || 'Location TBA' }}
                        </span>
                        <template v-if="neighborhood(event)">
                            <span class="events-row-sep">·</span>
                            <span class="events-row-hood">{{ neighborhood(event) }}</span>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Outside the link: it's a button, and nesting it inside an anchor
                 would make the whole row a favourite toggle for keyboard users. -->
            <GlobalUserActionButton class="events-row-fav" :item="event" list="ohcFavoriteEvents" />
        </li>
    </ul>
</template>

<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useEventDay } from '@/composables/eventDay';
    import { eventStatus } from '~/utils/eventStatus';

    defineProps({
        items: {
            type: Array,
            required: true,
        },
    });

    const { sitesMap } = storeToRefs(useSitesStore());
    const { neighborhoodsData } = storeToRefs(useNeighborhoodsStore());

    const dayLabel = (event) => useEventDay(event).eventDay?.label || '';

    // Sites store the display name in `Site`, not `Name` — Cards/event.vue reads
    // `.Name` here and so has never rendered a venue.
    const venue = (event) => {
        const site = sitesMap.value?.[event.fields?.Site?.[0]];
        return site?.fields?.Site || '';
    };

    const neighborhood = (event) => {
        const site = sitesMap.value?.[event.fields?.Site?.[0]];
        const id = site?.fields?.['OHC Neighborhood']?.[0];
        if (!id) return '';
        return (neighborhoodsData.value || []).find((n) => n.id === id)?.fields?.Name || '';
    };

    // Live/ended badges are only meaningful while the festival is running. Gated
    // to events happening today so the whole list doesn't read "Ended" the rest
    // of the year — which is what it would do against any previous year's data.
    const now = useState('events-rows-now', () => null);
    onMounted(() => {
        now.value = Date.now();
        const tick = setInterval(() => (now.value = Date.now()), 60_000);
        onBeforeUnmount(() => clearInterval(tick));
    });

    // The timing rules, and why every time is read as Chicago wall-clock,
    // live in utils/eventStatus.ts.
    const status = (event) => (now.value ? eventStatus(event.fields?.['Date'], event.fields?.['Time'], now.value) : '');
</script>

<style lang="scss" scoped>
    // The favourite button is absolutely positioned in the row's top-right, so
    // any text that can sit level with it has to reserve this much room: the
    // button's 34px plus a 10px gap. Shared so the title and description can't
    // drift apart and let text slide under it.
    $fav-reserve: 44px;

    // These tags are all-caps at 10px/1, so the line box keeps descender space
    // the glyphs never use — measured, the ink sat 5px from the top and 6px from
    // the bottom with symmetric 4px padding. Trimming a pixel off the bottom
    // makes the *optical* padding equal, which is what you actually see.
    @mixin caps-tag($x: 8px) {
        padding: 4px $x 3px;
    }

    .events-rows {
        list-style: none;
    }

    .events-row {
        position: relative;
        border-top: 1px solid $gray-light-2;

        &:last-child {
            border-bottom: 1px solid $gray-light-2;
        }
    }

    .events-row-link {
        display: block;
        padding: 20px 0;

        @media (min-width: $tablet) {
            display: grid;
            grid-template-columns: 190px 1fr;
            gap: 24px;
        }
    }

    .events-row-day {
        display: block;

        font-family: $body;
        font-size: 12px;
        font-weight: $medium;
        letter-spacing: 0.72px;
        line-height: 15px;
        text-transform: uppercase;
        color: $gray-darkest;
    }

    .events-row-when-inner {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 4px;

        // Column, not block: as a block the inline-block tag trailed the time on
        // whichever rows happened to fit, so the tag's position jumped row to row.
        @media (min-width: $tablet) {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
        }
    }

    .events-row-time {
        font-family: $title;
        font-size: 20px;
        font-weight: $bold;
        line-height: 1.1;
        color: $black;
    }

    .events-row-cat {
        display: inline-block;
        @include caps-tag;

        background: $turquoise-light;

        font-family: $body;
        font-size: 10px;
        font-weight: $heavy;
        letter-spacing: 0.6px;
        line-height: 1;
        text-transform: uppercase;
        color: $black;
    }

    .events-row-body {
        margin-top: 10px;

        @media (min-width: $tablet) {
            margin-top: 0;
        }
    }

    .events-row-title {
        padding-right: $fav-reserve;

        font-family: $title;
        font-size: 20px;
        font-weight: $bold;
        line-height: 1.1;
        text-transform: uppercase;
        color: $black;
    }

    // Stretches the title's link across the whole row, so the row stays one big
    // target without wrapping everything in an anchor.
    .events-row-title-link::after {
        content: '';
        position: absolute;
        inset: 0;
    }

    .events-row-status {
        display: inline-block;
        margin-left: 8px;
        @include caps-tag(7px);
        vertical-align: middle;

        font-family: $body;
        font-size: 10px;
        font-weight: $heavy;
        letter-spacing: 0.6px;
        text-transform: uppercase;

        &.is-live {
            background: $red;
            color: $white;
        }

        &.is-ended {
            background: $gray-light;
            color: $gray-dark;
        }
    }

    .events-row-desc {
        margin-top: 8px;

        // A short title leaves the description's first line level with the
        // favourite button, where it ran underneath it.
        padding-right: $fav-reserve;

        font-family: $body;
        font-size: 16px;
        font-weight: $book;
        line-height: 22px;
        color: $gray-darkest;

        // Long descriptions shouldn't push the rows out of rhythm.
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;

        // Editor markup arrives via v-html, so these need :deep(). Links have to
        // sit above the title's stretched overlay or the row link swallows them.
        :deep(a) {
            position: relative;
            z-index: 1;
            text-decoration: underline;
        }

        :deep(p) {
            display: inline;
        }
    }

    .events-row-venue-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 12px;
    }

    .events-row-venue {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        font-family: $body;
        font-size: 14px;
        font-weight: $medium;
        color: $black;
    }

    .events-row-pin {
        flex: 0 0 auto;
        width: 10px;
        height: auto;
    }

    .events-row-sep {
        color: $gray-common;
    }

    .events-row-hood {
        display: inline-block;
        @include caps-tag;

        background: $gray-light;

        font-family: $body;
        font-size: 10px;
        font-weight: $heavy;
        letter-spacing: 0.6px;
        line-height: 1;
        text-transform: uppercase;
        color: $gray-darkest;
    }

    .events-row-fav {
        position: absolute;
        top: 20px;
        right: 0;
        // Above the title link's stretched overlay, or it can't be clicked.
        z-index: 2;
    }
</style>
